import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint, FaCheckCircle, FaClock, FaMedkit, FaStore, FaExchangeAlt, FaFileAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

const DetailRebalance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/rebalance/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Gagal fetch detail rebalance");
        const result = await res.json();
        setDetail(result.data);
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "Gagal Mengambil Data",
          text: "Gagal mengambil data detail rebalance stok.",
          customClass: { popup: "rounded-3xl" },
        });
        navigate("/rebalance");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id, navigate, token]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handlePrint = () => {
    if (!detail) return;
    const printWin = window.open("", "_blank");
    if (!printWin) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>SURAT REBALANCE STOK - ${detail.code || "REBALANCE"}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; color: #111; margin: 0; padding: 0; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
            .header h1 { font-size: 18px; margin: 0; font-weight: bold; text-transform: uppercase; color: #065f46; }
            .header p { margin: 2px 0; font-size: 11px; color: #444; }
            .title { text-align: center; font-size: 14px; font-weight: bold; margin-bottom: 15px; text-decoration: underline; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px; }
            .table-data { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .table-data th, .table-data td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            .table-data th { background-color: #f0fdf4; color: #065f46; font-weight: bold; }
            .box { border: 1px solid #ddd; background: #fafafa; padding: 10px; border-radius: 6px; margin-bottom: 15px; }
            .signature-box { display: flex; justify-content: space-between; margin-top: 40px; padding: 0 30px; }
            .sig-col { text-align: center; width: 200px; }
            .sig-space { height: 60px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>APOTEK DJAYA MEDIKA</h1>
            <p>Jalan Raya Utama Apotek Djaya Medika | Telp: (021) 555-0199</p>
            <p>Email: admin@djayamedika.com | SIPA: 19910704/SIPA035.06/2024/2859</p>
          </div>

          <div class="title">SURAT MUTASI REBALANCE STOK OBAT</div>

          <div class="grid">
            <div>
              <strong>Kode Transaksi:</strong> ${detail.code || "-"}<br>
              <strong>Tanggal Mutasi:</strong> ${formatDate(detail.created_at)}<br>
              <strong>Arah Tipe Mutasi:</strong> ${(detail.type || "").toUpperCase()}
            </div>
            <div>
              <strong>Outlet Cabang:</strong> ${detail.outlet?.name || "-"}<br>
              <strong>Tujuan (Objective):</strong> ${(detail.objective || "").toUpperCase()}<br>
              <strong>Status Approval:</strong> ${detail.approved ? "DISETUJUI (APPROVED)" : "PENDING (BELUM APPROVE)"}
            </div>
          </div>

          <table class="table-data">
            <thead>
              <tr>
                <th>Nama Obat Master</th>
                <th style="text-align: center;">Stok Sebelum</th>
                <th style="text-align: center;">Jumlah Mutasi</th>
                <th style="text-align: center;">Stok Setelah</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>${detail.obat?.name || "-"}</strong></td>
                <td style="text-align: center;">${detail.stock_before ?? "-"}</td>
                <td style="text-align: center; font-weight: bold;">${detail.change || detail.stock || "-"}</td>
                <td style="text-align: center;">${detail.stock_after ?? "-"}</td>
              </tr>
            </tbody>
          </table>

          <div class="box">
            <strong>Catatan / Alasan Mutasi:</strong>
            <p style="margin: 4px 0 0 0;">${detail.message || "Tidak ada catatan."}</p>
          </div>

          <div class="signature-box">
            <div class="sig-col">
              <p>Petugas Yang Mengajukan,</p>
              <div class="sig-space"></div>
              <p>( .................................... )</p>
            </div>
            <div class="sig-col">
              <p>Apoteker Penanggung Jawab,</p>
              <div class="sig-space"></div>
              <p><strong>apt. Wahyu Wijayanti, S.Farm</strong><br><small>No SIPA: 19910704/SIPA035.06/2024/2859</small></p>
            </div>
          </div>
        </body>
      </html>
    `;

    printWin.document.write(html);
    printWin.document.close();
    printWin.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="bg-white rounded-3xl p-12 text-center text-gray-400 font-semibold border border-gray-100">
        Data detail rebalance tidak ditemukan.
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Action Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between gap-4"
      >
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
        >
          <FaArrowLeft className="w-3.5 h-3.5" />
          <span>Kembali</span>
        </button>

        <div className="flex items-center gap-3">
          <span
            className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase border flex items-center gap-1.5 ${
              detail.approved
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            {detail.approved ? (
              <>
                <FaCheckCircle className="w-3.5 h-3.5" /> Disetujui
              </>
            ) : (
              <>
                <FaClock className="w-3.5 h-3.5" /> Pending Approval
              </>
            )}
          </span>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
          >
            <FaPrint className="w-3.5 h-3.5" />
            <span>Cetak Surat Rebalance</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Main Detail Content Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 md:p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Detail Mutasi Rebalance #{detail.id}
            </span>
            <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
              <FaFileAlt className="text-emerald-600 w-5 h-5" />
              <span>Kode: {detail.code || "-"}</span>
            </h1>
          </div>
          <span className="px-3 py-1 bg-emerald-50 text-emerald-800 font-mono text-xs font-bold rounded-xl border border-emerald-200 uppercase">
            {detail.type === "in" ? "STOK MASUK (IN)" : "STOK KELUAR (OUT)"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Kolom Kiri - Data Specs */}
          <div className="md:col-span-2 space-y-6 text-xs">
            {/* Informati Obat */}
            <div className="p-5 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-3">
              <h2 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
                <FaMedkit className="text-emerald-600" /> Informasi Obat
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Nama Obat</span>
                  <p className="font-extrabold text-gray-900 text-sm">{detail.obat?.name || "-"}</p>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Tujuan (Objective)</span>
                  <p className="font-extrabold text-emerald-700 text-sm uppercase">
                    {detail.objective || "-"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Stok Sebelum</span>
                  <p className="font-mono font-bold text-gray-700">{detail.stock_before ?? "-"}</p>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Stok Setelah</span>
                  <p className="font-mono font-extrabold text-emerald-700">{detail.stock_after ?? "-"}</p>
                </div>
              </div>
            </div>

            {/* Detail Transaksi & Outlet */}
            <div className="p-5 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-3">
              <h2 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
                <FaStore className="text-emerald-600" /> Detail Outlet & Waktu
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Outlet Cabang</span>
                  <p className="font-bold text-gray-800">{detail.outlet?.name || "-"}</p>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Tanggal Dibuat</span>
                  <p className="font-mono font-semibold text-gray-700">{formatDate(detail.created_at)}</p>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Status Approval</span>
                  <p className="font-bold text-gray-800">{detail.approved ? "Sudah Disetujui" : "Belum Disetujui"}</p>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Jumlah Perubahan</span>
                  <p className="font-mono font-extrabold text-emerald-700">{detail.change || detail.stock || "-"}</p>
                </div>
              </div>
            </div>

            {/* Catatan / Pesan */}
            <div className="p-5 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-2">
              <h2 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
                <FaExchangeAlt className="text-emerald-600" /> Catatan / Alasan Mutasi
              </h2>
              <p className="text-gray-700 leading-relaxed italic bg-white p-3 rounded-xl border border-gray-200">
                "{detail.message || "Tidak ada pesan atau catatan tambahan."}"
              </p>
            </div>
          </div>

          {/* Kolom Kanan - Evidence Photo */}
          <div className="md:col-span-1 space-y-3 text-xs">
            <h2 className="text-sm font-extrabold text-gray-900">Foto Bukti / Evidence</h2>
            {detail.evidence ? (
              <div className="p-3 bg-gray-50 rounded-2xl border border-gray-200 space-y-2">
                <img
                  src={detail.evidence}
                  alt="Foto Evidence Rebalance"
                  className="w-full max-h-72 object-contain rounded-xl shadow-sm bg-white"
                />
                <p className="text-[10px] text-center text-gray-400 font-semibold">
                  Foto bukti fisik mutasi stok
                </p>
              </div>
            ) : (
              <div className="h-64 bg-gray-50 rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 space-y-1">
                <FaFileAlt className="w-8 h-8 opacity-40 text-emerald-600" />
                <span className="font-semibold text-xs">Tidak Ada Foto Bukti</span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DetailRebalance;
